export const asArray = (value) => Array.isArray(value) ? value : [];

export const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export const pct = (value, total) => total ? (num(value) / num(total)) * 100 : 0;

export const fmtPct = (value, digits = 1) => `${num(value).toFixed(digits)}%`;
export const fmtScore = (value) => num(value).toFixed(2);

export const ratingCounts = () => ({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });

export const distributionFromCounts = (counts) => {
  const total = Object.values(counts).reduce((a, b) => a + num(b), 0);
  return {
    1: pct(counts[1], total),
    2: pct(counts[2], total),
    3: pct(counts[3], total),
    4: pct(counts[4], total),
    5: pct(counts[5], total),
    total,
  };
};

export const measuresFromCounts = (counts) => {
  const total = Object.values(counts).reduce((a, b) => a + num(b), 0);
  return {
    moreThan4: pct(counts[5], total),
    exactly4: pct(counts[4], total),
    lessThan4: pct(num(counts[1]) + num(counts[2]) + num(counts[3]), total),
    lessThan3: pct(num(counts[1]) + num(counts[2]), total),
    fourOrMore: pct(num(counts[4]) + num(counts[5]), total),
  };
};

export const pointsFromCounts = (counts) =>
  [1, 2, 3, 4, 5].reduce((sum, rating) => sum + rating * num(counts[rating]), 0);

export const averageFromCounts = (counts) => {
  const total = Object.values(counts).reduce((a, b) => a + num(b), 0);
  return total ? pointsFromCounts(counts) / total : 0;
};

export const dateLabel = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

export const surveyPeriod = (data, fromDate, toDate) => {
  if (fromDate || toDate) return `${dateLabel(fromDate)} – ${dateLabel(toDate)}`;
  if (data?.surveyPeriod) return String(data.surveyPeriod).replaceAll('/', ' / ');
  if (data?.reportPeriod?.fromDate || data?.reportPeriod?.toDate) {
    return `${dateLabel(data.reportPeriod.fromDate)} – ${dateLabel(data.reportPeriod.toDate)}`;
  }
  if (data?.survey?.startDate || data?.survey?.endDate) {
    return `${dateLabel(data.survey.startDate)} – ${dateLabel(data.survey.endDate)}`;
  }
  return 'All available responses';
};

export const normalizeAverageCategories = (data) => asArray(data?.categories).map((category) => {
  const questions = asArray(category.questions).map((q) => {
    const rd = q.ratingDistribution || {};
    const counts = ratingCounts();
    [1,2,3,4,5].forEach((r) => {
      const countValue = rd[`rating${r}Count`] ?? rd[`Rating${r}Count`];
      const percentValue = rd[`rating${r}`] ?? rd[`Rating${r}`];
      counts[r] = countValue !== undefined && countValue !== null
        ? num(countValue)
        : (num(percentValue) / 100) * num(q.responseCount);
    });
    return {
      ...q,
      questionText: q.questionText || 'Untitled question',
      averageScore: num(q.averageScore),
      responseCount: num(q.responseCount),
      counts,
      distribution: distributionFromCounts(counts),
      measures: measuresFromCounts(counts),
      totalPoints: pointsFromCounts(counts),
    };
  });
  return {
    ...category,
    categoryName: category.categoryName || 'Unnamed Category',
    averageScore: num(category.averageScore),
    questions,
  };
});

export const aggregateEmployeeSurvey = (data) => {
  const categories = new Map();
  asArray(data?.employeeReports).forEach((employeeReport) => {
    asArray(employeeReport.categoryWiseData).forEach((category) => {
      const key = String(category.categoryId ?? category.categoryName);
      if (!categories.has(key)) {
        categories.set(key, { categoryId: category.categoryId, categoryName: category.categoryName || 'Unnamed Category', questions: new Map() });
      }
      const target = categories.get(key);
      asArray(category.questions).forEach((question) => {
        if (!target.questions.has(question.questionId)) {
          target.questions.set(question.questionId, { questionId: question.questionId, questionText: question.questionText || 'Untitled question', counts: ratingCounts() });
        }
        const q = target.questions.get(question.questionId);
        const rating = num(question.ratingValue);
        if (rating >= 1 && rating <= 5) q.counts[rating] += 1;
      });
    });
  });

  return Array.from(categories.values()).map((category) => {
    const questions = Array.from(category.questions.values()).map((q) => ({
      ...q,
      responseCount: Object.values(q.counts).reduce((a,b) => a + b, 0),
      totalPoints: pointsFromCounts(q.counts),
      averageScore: averageFromCounts(q.counts),
      distribution: distributionFromCounts(q.counts),
      measures: measuresFromCounts(q.counts),
    }));
    const counts = ratingCounts();
    questions.forEach((q) => [1,2,3,4,5].forEach((r) => { counts[r] += q.counts[r]; }));
    return {
      categoryId: category.categoryId,
      categoryName: category.categoryName,
      questions,
      counts,
      totalQuestions: questions.length,
      totalResponses: Object.values(counts).reduce((a,b) => a+b, 0),
      totalPoints: pointsFromCounts(counts),
      averageScore: averageFromCounts(counts),
      distribution: distributionFromCounts(counts),
      measures: measuresFromCounts(counts),
    };
  });
};

export const flattenQuestions = (categories) => asArray(categories).flatMap((category) => asArray(category.questions).map((q) => ({ ...q, categoryName: category.categoryName })));
